import { translateBackend } from '@assets/i18n/config';
import ExternalInstitutionSelect from '@components/inputs/externalInstitutionSelect';
import type { SubAccount } from '@interfaces/goalSubAccountTypes';
import type { TranslatedString } from '@interfaces/shared';
import type { Institution } from '@interfaces/transfer';
import { ExternalTransferTypes } from '@interfaces/transfer';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Grid, MenuItem } from '@mui/material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import { Form } from '@ovComponents/2-component/form/form';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { AmountField } from '@ovComponents/3-pattern/amountField/amountField';
import { SubAccountSelect } from '@ovComponents/3-pattern/subaccountSelect/subAccountSelect';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext } from '@providers/userContextProvider';
import { dollars2cents } from '@utils/dollars2cents';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export type ExternalTransfer = {
  amountCents: number;
  institution?: Partial<Institution>;
  subAccount?: SubAccount;
  transferAccountNumber: string;
  transferMethod: string;
};
type ExternalTransferDialogContentProps = {
  onTransferCreated: (externalTransfer: ExternalTransfer) => void;
  userData: any;
  onDialogClose: () => void;
  disclaimer?: TranslatedString | null;
};

const ExternalTransferDialogContent = ({
  onTransferCreated,
  userData,
  onDialogClose,
  disclaimer,
}: ExternalTransferDialogContentProps) => {
  const { t } = useTranslation(['workflowCompletions', 'affiliationTypes']);
  const [inboundAccount, setInboundAccount] = useState<
    SubAccount | undefined
  >();
  const [transferMethod, setTransferMethod] = useState<ExternalTransferTypes>(
    ExternalTransferTypes.AllInCash,
  );
  const [institution, setInstitution] = useState<
    Partial<Institution> | undefined
  >();
  const [externalAccountNumber, setExternalAccountNumber] = useState('');
  const [amount, setAmount] = useState('');
  const { activeOrganization } = useContext(UserContext);
  const enableInKind: boolean =
    activeOrganization.availableFeatureFlags?.includes('IN_KIND_TRANSFERS') ??
    false;
  const { sys } = useThemeTokens();
  const submit = () => {
    onTransferCreated({
      amountCents: dollars2cents(amount),
      institution,
      subAccount: inboundAccount,
      transferAccountNumber: externalAccountNumber,
      transferMethod,
    });
  };
  return (
    <Form onSubmit={submit}>
      <Box display='flex' flexDirection='row' justifyContent='end'>
        <Box sx={{ cursor: 'pointer' }} display='flex' onClick={onDialogClose}>
          <Icon icon={CloseRoundedIcon} size='medium' />
        </Box>
      </Box>
      <Typography variant='headingMedium' sx={{ mt: 1 }}>
        {t('workflowCompletions:externalTransferCreation.title')}
      </Typography>
      <SubAccountSelect
        sx={{ mt: '24px' }}
        omitAny
        selectedSubAccount={inboundAccount}
        onSubAccountSelect={setInboundAccount}
        userId={userData.id}
        label={t('workflowCompletions:inboundAccount')}
      />
      <SelectField
        onChange={(e: any) => setTransferMethod(e.target.value)}
        label={t('workflowCompletions:transferMethod')}
        fullWidth
        sx={{ mt: '24px' }}
        value={transferMethod}
      >
        <MenuItem value='ALL_IN_CASH'>
          {t('workflowCompletions:transferMethodOptions.ALL_IN_CASH')}
        </MenuItem>
        <MenuItem value='PARTIAL_CASH'>
          {t('workflowCompletions:transferMethodOptions.PARTIAL_CASH')}
        </MenuItem>
        {enableInKind && (
          <MenuItem value='ALL_IN_KIND'>
            {t('workflowCompletions:transferMethodOptions.ALL_IN_KIND')}
          </MenuItem>
        )}
      </SelectField>
      <Grid
        container
        display='flex'
        flexDirection='row'
        sx={{ mt: '20px' }}
        justifyContent='space-between'
        spacing={1}
        alignItems='end'
      >
        <Grid item xs={8}>
          <ExternalInstitutionSelect
            setInstitution={(e) => setInstitution(e)}
            value={institution?.id ?? ''}
            label={t('workflowCompletions:externalFinancialInstitution')}
            useOvComponents
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            onChange={(e: any) => setExternalAccountNumber(e.target.value)}
            label={t('workflowCompletions:externalAccount')}
            fullWidth
            value={externalAccountNumber}
          />
        </Grid>
      </Grid>
      <Grid
        container
        display='flex'
        flexDirection='row'
        sx={{ mt: '20px' }}
        justifyContent='space-between'
        spacing={1}
        alignItems='end'
      >
        <Grid item xs={4}>
          <AmountField setAmount={(e: any) => setAmount(e)} amount={amount} />
        </Grid>
      </Grid>
      <br></br>
      {disclaimer && (
        <Box
          sx={{
            mb: 1,
            background: sys.color.surfaceContainerVariant,
            borderRadius: sys.borderRadius.lg,
            p: 2,
          }}
        >
          <Typography variant='bodySmall'>
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackend(disclaimer)}
            </ReactMarkdown>
          </Typography>
        </Box>
      )}
      <Box width='100%' display='flex' justifyContent='end'>
        <Button
          color='primary'
          label={t('workflowCompletions:addTransfer')}
          disabled={false}
          sx={{ mt: 3 }}
          type='submit'
        />
      </Box>
    </Form>
  );
};

export default ExternalTransferDialogContent;
